import { useCallback, useMemo, useState } from "react";

import { useMutation } from "@apollo/client";
import { useFormikContext } from "formik";

import LawyerSelectOption from "components/ProfileEditor/counsel/LawyerSelectOption";
import { getInitialValues } from "components/ProfileEditor/parties/lawyer/lawyerFormHelpers";
import Button from "components/common/Button";
import CreatableSelect from "components/common/inputs/Select/CreatableSelect";
import TextInput from "components/common/inputs/TextInput";

import useCourtInformation from "hooks/me/useCourtInformation";

import CREATE_LAWYER from "@gql/sanity/mutations/createLawyer";

const CreateLawyerSelect = ({ user, lawyers, refetch, disabled }) => {
  const { setFieldValue, setValues, values } = useFormikContext();

  const { sanityId, isSelfRepresented } = values;

  const { provinceShorthand: residence } = useCourtInformation(user?.client);

  const [createLawyer, { loading: createLawyerLoading }] = useMutation(CREATE_LAWYER);

  const [isEditing, setIsEditing] = useState(false);

  const toggleEditing = () => setIsEditing((prev) => !prev);

  const handleCreateLawyer = async (name) => {
    const createdLawyer = await createLawyer({
      variables: { data: { name, location: { create: { residence, country: "Canada" } } } },
    });

    await refetch();

    setValues(getInitialValues(createdLawyer.data.createLawyer, isSelfRepresented));
  };

  const handleChange = useCallback(
    (selectedOption) => {
      setFieldValue("sanityId", selectedOption.value);

      const selectedLawyer = lawyers?.find((lawyer) => lawyer?._id === selectedOption.value);

      if (selectedLawyer) {
        const newValues = getInitialValues(selectedLawyer, isSelfRepresented);
        setValues(newValues);
      }
    },
    [lawyers, setValues, setFieldValue, isSelfRepresented],
  );

  const selectOptions = useMemo(() => {
    return (
      lawyers?.map((lawyer) => ({
        label: lawyer?.name,
        value: lawyer?._id,
        city: lawyer?.city,
        orgName: lawyer?.orgName,
      })) || []
    );
  }, [lawyers]);

  return (
    <div className="create-lawyer-select-container">
      <div className="select-actions">
        <Button variant="link" onClick={toggleEditing}>
          {isEditing ? "Close" : "Edit Name"}
        </Button>
      </div>
      {isEditing && (
        <TextInput
          containerClassName="mb-3 mt-3"
          name="name"
          autoFocus
          label="Edit Name"
          placeholder="Lawyer Name"
        />
      )}
      {!isEditing && (
        <CreatableSelect
          label="Name"
          placeholder="Select or Create a Lawyer"
          containerClassName="mb-3 mt-3"
          key="sanityId"
          name="sanityId"
          value={
            sanityId && sanityId !== "non-sanity" ? { value: sanityId, label: values?.name } : null
          }
          handleChange={handleChange}
          onCreate={handleCreateLawyer}
          disabled={disabled || createLawyerLoading}
          options={selectOptions}
          components={{ Option: LawyerSelectOption }}
        />
      )}
    </div>
  );
};

export default CreateLawyerSelect;
